import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'src/shadcn/components/ui/form';
import { Input } from 'src/shadcn/components/ui/input';
import { gql } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { useHotToast } from 'src/utils/useHotToast';
import { z } from 'zod';
import { useAuthContext } from 'src/Components/AuthContext';
import { useLoginAsEmployeeLazyQuery } from './__generated__/ShopLoginPage.generated';
import { Button } from 'src/shadcn/components/ui/button';
import { RotateCw } from 'lucide-react';
import { AuthPage } from './AuthPage';
import { AuthPageRightSection } from './AuthPageRightSection';
import { AuthPageLeftSection } from './AuthPageLeftSection';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'src/shadcn/components/ui/card';
import { PostEmployeeLoginDialog } from './PostEmployeeLoginDialog';
import { AuthTokenType } from 'src/graphql/__generated__/globalTypes';
import { GearIcon } from '@radix-ui/react-icons';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/shadcn/components/ui/dialog';

export interface IShopLoginPageProps {}

function useUserInformationForm() {
  const form = useForm({
    resolver: zodResolver(
      z.object({
        employeeId: z
          .string()
          .min(1)
          .refine(value => /^(E\d+|e\d+|\d+)$/.test(value), {
            message: 'Invalid employee ID',
          })
          .transform(v => v.replace(/E/gi, '')),
      })
    ),
    defaultValues: {
      employeeId: '',
    },
  });
  return { form };
}

export const ShopLoginPage: React.FunctionComponent<
  IShopLoginPageProps
> = props => {
  const { form } = useUserInformationForm();
  const {
    loginAsEmployee,
    isLoggingInAsEmployee,
    isStartShiftModalOpen,
    setIsStartShiftModalOpen,
  } = useLoginAsEmployee();
  const authContext = useAuthContext();
  return (
    <>
      <AuthPage>
        <AuthPageLeftSection>
          <div className='relative z-20 mt-auto'>
            <Dialog>
              <DialogTrigger asChild>
                <Button variant='ghost'>
                  <GearIcon />
                  <span className='sr-only'>Settings</span>
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Log out from shop?</DialogTitle>
                  <DialogDescription>
                    This action will log out the entire account. No employee
                    will be able to log in after until the admin logs in again.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button
                    variant={'destructive'}
                    onClick={() => {
                      authContext.logout();
                    }}
                  >
                    <span>Log out</span>
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </AuthPageLeftSection>
        <AuthPageRightSection>
          <Card className='mt-10 gap-8 border-none shadow-none'>
            <CardHeader>
              <CardTitle>Employee log in</CardTitle>
              <CardDescription>
                {authContext.user?.user.companyName} @{' '}
                {authContext.user?.shopLocation?.name}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(data => {
                    loginAsEmployee(data.employeeId);
                    form.reset();
                  })}
                  className='space-y-6'
                >
                  <FormField
                    control={form.control}
                    name='employeeId'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Employee ID</FormLabel>
                        <FormControl>
                          <Input
                            placeholder='Your employee ID'
                            {...field}
                            type='password'
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className='flex justify-end gap-3'>
                    {/* <Button
                      variant={'ghost'}
                      onClick={() => authContext.logout()} // TODO: add are you sure dialog
                    >
                      Log out
                    </Button> */}
                    <Button type='submit'>
                      <span>Continue</span>
                      {isLoggingInAsEmployee && (
                        <RotateCw className='ml-2 h-4 w-4 animate-spin' />
                      )}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </AuthPageRightSection>
      </AuthPage>
      <PostEmployeeLoginDialog
        isOpen={isStartShiftModalOpen}
        onOpenChange={setIsStartShiftModalOpen}
        onComplete={() => {
          setIsStartShiftModalOpen(false);
          authContext.setForcedScreen(undefined);
        }}
        key={authContext.user?.employee?.id}
      />
    </>
  );
};

function useLoginAsEmployee() {
  const toast = useHotToast();
  const [isStartShiftModalOpen, setIsStartShiftModalOpen] =
    React.useState(false);
  const authContext = useAuthContext();
  const [loginAsEmployee, { loading: isLoggingInAsEmployee, data }] =
    useLoginAsEmployeeLazyQuery({
      onError: e => {
        toast.error(e.message);
      },
      onCompleted: res => {
        authContext.setForcedScreen(AuthTokenType.SHOP);
        authContext.onLogin(res.loginAsEmployee);
        setIsStartShiftModalOpen(true);
      },
    });

  return {
    loginAsEmployee: (employeeId: string) =>
      loginAsEmployee({ variables: { employeeId } }),
    isLoggingInAsEmployee,
    isStartShiftModalOpen,
    setIsStartShiftModalOpen,
    loginAsEmployeeData: data?.loginAsEmployee,
  };
}

const LOGIN_AS_ADMIN = gql`
  query LoginAsEmployee($employeeId: String!) {
    loginAsEmployee(employeeId: $employeeId) {
      user {
        id
        companyName
        authUserType
      }
      token
      authTokenType
      shopLocation {
        id
        name
      }
      employee {
        id
        firstName
        lastName
      }
    }
  }
`;
